import type { ReactNode } from 'react';
import { Link, useLocation, type To } from 'react-router';
import { recordHubTrip } from '@traceio-org/auth-ui';
import { hubUrl } from '@traceio-org/platform-contracts';
import { EmptyPageState, NavigationGlyph, PageAction } from '@traceio-org/ui';
import type { AdminFailure } from '../api';
import { useAdmin } from '../context';

/** An in-app link that looks like the library's page actions (PageAction only renders plain anchors). */
export function ActionLink({
  to,
  primary = false,
  children,
}: {
  to: To;
  primary?: boolean;
  children: ReactNode;
}) {
  return (
    <Link className={primary ? 'admin-action admin-action--primary' : 'admin-action'} to={to}>
      {children}
    </Link>
  );
}

export interface AdminProblemProps {
  failure: AdminFailure;
  /** What could not be read, as the title says it: "organizations" or "this organization". */
  subject: string;
  onRetry?: () => void;
  /** Replaces the default actions, for failures the page handles itself (not found, invalid request). */
  action?: ReactNode;
  /**
   * Set when a refusal (403) may come from the missing support access grant: the caller is not a platform
   * admin and holds no grant for the organization. The page then says how to get one.
   */
  supportAccess?: { canRequest: boolean; requestPath: string } | undefined;
}

function Reference({ value }: { value: string | null }) {
  return value ? (
    <p className="admin-reference">
      Support reference: <code>{value}</code>
    </p>
  ) : null;
}

/**
 * The state a page shows when a staff read failed, with the way forward for each failure. Sign-in links
 * go to the hub's Trace Admin sign-in with the current page as the return path; the click records the trip
 * (recordHubTrip) so the gate recognizes the way back and never loops. MFA is asked again only when
 * platform-api said so (reason mfa_required). Rendered as an alert: it replaces content the person was
 * waiting for.
 */
export function AdminProblem({ failure, subject, onRetry, action, supportAccess }: AdminProblemProps) {
  const { hubOrigin } = useAdmin();
  const location = useLocation();
  const signInUrl = hubUrl(hubOrigin, {
    route: 'login',
    productKey: 'admin',
    returnPath: `${location.pathname}${location.search}`,
  });
  const retry = onRetry ? (
    <PageAction primary onClick={onRetry}>
      Try again
    </PageAction>
  ) : null;

  let title: string;
  let description: ReactNode;
  let actions: ReactNode = null;
  switch (failure.kind) {
    case 'session-ended':
      title = 'Your session has ended';
      description = 'Sign in again through Trace to continue where you were.';
      actions = (
        <PageAction primary href={signInUrl} onClick={() => recordHubTrip()}>
          Sign in again
        </PageAction>
      );
      break;
    case 'mfa-required':
      title = 'Sign in again with multi-factor authentication';
      description = 'Trace Admin needs a session that was established with multi-factor authentication.';
      actions = (
        <PageAction
          primary
          href={signInUrl}
          onClick={() => recordHubTrip({ reauthenticate: 'mfa_required' })}
        >
          Sign in again
        </PageAction>
      );
      break;
    case 'not-staff':
      title = 'Your Trace staff access has ended';
      description =
        'Trace no longer lists this account as active staff, so Trace Admin cannot show anything more. Sign out, then sign in with an account that has staff access.';
      actions = (
        <PageAction primary href={hubUrl(hubOrigin, { route: 'logout', productKey: 'admin' })}>
          Sign out
        </PageAction>
      );
      break;
    case 'forbidden':
      if (supportAccess) {
        title = `Reading ${subject} needs support access`;
        description = supportAccess.canRequest
          ? "Trace staff other than platform admins read an organization's data only during a time-limited support access grant. Every read is recorded with your name and reason."
          : "Trace staff other than platform admins read an organization's data only during a time-limited support access grant, and your staff role cannot request one. Ask a support operator or a platform admin.";
        if (supportAccess.canRequest) {
          actions = (
            <ActionLink primary to={supportAccess.requestPath}>
              Request support access
            </ActionLink>
          );
        }
        break;
      }
      title = `Your staff role cannot read ${subject}`;
      description = 'Ask a platform admin for the access you need.';
      break;
    case 'not-found':
      title = `We could not find ${subject}`;
      description = 'It may have been removed, or the address is wrong.';
      break;
    case 'invalid-request':
      title = `We could not load ${subject}`;
      description = 'Trace did not accept this request. The address may be out of date.';
      break;
    case 'rate-limited':
      title = 'Too many requests';
      description = (
        <>
          <p>Wait a moment, then try again.</p>
          <Reference value={failure.reference} />
        </>
      );
      actions = retry;
      break;
    case 'unavailable':
      title = `We could not load ${subject}`;
      description = (
        <>
          <p>
            Trace could not reach the platform API, or its answer could not be used. Try again in a moment.
          </p>
          <Reference value={failure.reference} />
        </>
      );
      actions = retry;
      break;
  }

  return (
    <div className="admin-problem" role="alert" data-failure={failure.kind}>
      <EmptyPageState
        icon={<NavigationGlyph name="FileWarning" />}
        title={title}
        description={description}
        action={action ?? actions}
      />
    </div>
  );
}
